import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getDisplayName as getDisplayNameFromMember, UNKNOWN_USER } from '../lib/member-display';
import { getLogoutEpoch } from '../lib/logout-teardown-signal';
import { isSignedOutReadFailure } from '../lib/post-logout-denial';
import { supabase } from '../integrations/supabase/client';

import type { OrganizationMemberWithProfile } from './organization-types';

// Stable identity for the disabled/loading fallback — a fresh `[]` per render
// would defeat the memoized return object and re-render every consumer.
const EMPTY_MEMBERS: OrganizationMemberWithProfile[] = [];

export function useOrganizationMembers(
  currentOrganizationId: string | null,
  user: { id: string } | null,
  isLoadingOrganizations: boolean,
) {
  const queryClient = useQueryClient();

  const {
    data: members = EMPTY_MEMBERS,
    isLoading: isLoadingMembers,
    refetch,
  } = useQuery({
    queryKey: ['organization-members-cache', currentOrganizationId, user?.id],
    queryFn: async (): Promise<OrganizationMemberWithProfile[]> => {
      if (!currentOrganizationId || currentOrganizationId === 'admin') {
        return [];
      }

      const logoutEpoch = getLogoutEpoch();
      // Single query via team_members view (server-side JOIN of
      // organization_members + profiles). Replaces the previous 2-query +
      // client-side O(n^2) .find() approach.
      const { data, error } = await supabase
        .from('team_members')
        .select(
          'id, user_id, organization_id, role, status, joined_at, created_at, updated_at, first_name, last_name, email, avatar_url',
        )
        .eq('organization_id', currentOrganizationId);

      if (error) {
        if (!(await isSignedOutReadFailure(error, logoutEpoch))) {
          console.error('Error fetching team members:', error.message);
        }
        throw error;
      }

      // Sort alphabetically by first name, then last name, then email
      return (data ?? [])
        .map((row) => ({
          ...row,
          email: row.email || 'Unknown',
          first_name: row.first_name || null,
          last_name: row.last_name || null,
          avatar_url: row.avatar_url || null,
        }))
        .sort((a, b) => {
          const firstNameA = (a.first_name || '').toLowerCase();
          const firstNameB = (b.first_name || '').toLowerCase();
          if (firstNameA !== firstNameB) {
            return firstNameA.localeCompare(firstNameB);
          }

          const lastNameA = (a.last_name || '').toLowerCase();
          const lastNameB = (b.last_name || '').toLowerCase();
          if (lastNameA !== lastNameB) {
            return lastNameA.localeCompare(lastNameB);
          }

          return (a.email || '').localeCompare(b.email || '');
        });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    // `true` respects staleTime; 'always' would refetch on every observer
    // remount (e.g. a hidden tab shown again remounts its observers).
    refetchOnMount: true,
    // Only run after: user is authenticated, organizations loaded, and org ID is set
    enabled:
      !!user &&
      !isLoadingOrganizations &&
      !!currentOrganizationId &&
      currentOrganizationId !== 'admin',
  });

  // Derived: active members only
  const activeMembers = useMemo(() => members.filter((m) => m.status === 'active'), [members]);

  // Create lookup maps for O(1) access
  const memberByIdMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const memberByUserIdMap = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members]);

  // Email lookup is case-insensitive — RFC 5321 local-part is technically
  // case-sensitive but virtually no provider preserves casing in practice,
  // and matching against `email_metadata.from_email` (mixed casing from
  // user input) requires normalization.
  const memberByEmailMap = useMemo(
    () => new Map(members.map((m) => [m.email.toLowerCase(), m])),
    [members],
  );

  // Helper functions
  const getMemberById = useCallback(
    (memberId: string) => memberByIdMap.get(memberId),
    [memberByIdMap],
  );

  const getMemberByUserId = useCallback(
    (userId: string) => memberByUserIdMap.get(userId),
    [memberByUserIdMap],
  );

  const getMemberByEmail = useCallback(
    (email: string) => memberByEmailMap.get(email.toLowerCase()),
    [memberByEmailMap],
  );

  const getMemberAvatar = useCallback(
    (userId: string): string | null => {
      return memberByUserIdMap.get(userId)?.avatar_url || null;
    },
    [memberByUserIdMap],
  );

  const getMemberDisplayName = useCallback(
    (userId: string): string => {
      const member = memberByUserIdMap.get(userId);
      if (!member) {
        return UNKNOWN_USER;
      }
      return getDisplayNameFromMember(member);
    },
    [memberByUserIdMap],
  );

  const getMembersByRole = useCallback(
    (roles: string[]): OrganizationMemberWithProfile[] => {
      const roleSet = new Set(roles);
      return members.filter((m) => roleSet.has(m.role));
    },
    [members],
  );

  const invalidateMembers = useCallback(async () => {
    if (currentOrganizationId && currentOrganizationId !== 'admin') {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['organization-members-cache', currentOrganizationId, user?.id],
        }),
        // Also invalidate the old hook's query keys for backwards compatibility
        queryClient.invalidateQueries({
          queryKey: ['organization-memberships', currentOrganizationId],
        }),
      ]);
    }
  }, [queryClient, currentOrganizationId, user?.id]);

  const refreshMembers = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      members,
      activeMembers,
      isLoadingMembers,
      getMemberById,
      getMemberByUserId,
      getMemberByEmail,
      getMemberAvatar,
      getMemberDisplayName,
      getMembersByRole,
      refreshMembers,
      invalidateMembers,
    }),
    [
      members,
      activeMembers,
      isLoadingMembers,
      getMemberById,
      getMemberByUserId,
      getMemberByEmail,
      getMemberAvatar,
      getMemberDisplayName,
      getMembersByRole,
      refreshMembers,
      invalidateMembers,
    ],
  );
}
